import {
  LayoutDashboard,
  FileText,
  Receipt,
  AlertTriangle,
  BookOpen,
  Hash,
  FileSignature,
  Banknote,
  Truck,
  Users as UsersIcon,
  Settings,
  Settings2,
  HelpCircle,
  Mail,
  ListChecks,
  Wallet,
  Coins,
  PackageSearch,
  Undo2,
  RotateCcw,
  ShieldCheck,
  Calendar,
  FileCheck,
  FileSearch,
  Landmark,
  ChartNoAxesCombined,
  type LucideIcon,
} from "lucide-react";

export type PageGroupKey =
  | "profit_loss"
  | "expenses"
  | "upcoming_payments"
  | "contracts"
  | "cash_receipts"
  | "courier_invoices"
  | "collection_pods"
  | "email_log"
  | "bank_reconciliation"
  | "bank_reconciliation_manage"
  | "bank_reconciliation_signoff"
  | "users_settings";

export interface PageItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /**
   * If true, only admin / super-admin can see and use this item. Regular
   * users with the group permission don't see it in the sidebar and the
   * route is admin-gated. Used inside the Expenses group so non-admins
   * see only the Receipts (upload) page.
   */
  adminOnly?: boolean;
}

export interface PageGroup {
  key: PageGroupKey;
  label: string;
  icon: LucideIcon;
  description: string;
  /** Admin landing page for this group (first thing an admin sees). */
  defaultHref: string;
  /** Children rendered as a tree under the group in the sidebar. */
  items: PageItem[];
  /** Extra route prefixes that should also be gated by this group's permission. */
  extraRoutePrefixes?: string[];
  /**
   * If true, the group is open to every authenticated user regardless of
   * their pagePermissions list (no page-group gate). Used for operator-wide
   * tools like Collection PODs.
   */
  ungated?: boolean;
  /** Permission group used when this navigation category is presentation-only. */
  accessGroupKey?: PageGroupKey;
  /** Assignable permission only; no navigation tile or sidebar entry. */
  navHidden?: boolean;
}

export const PAGE_GROUPS: PageGroup[] = [
  {
    key: "profit_loss",
    label: "P&L's",
    icon: ChartNoAxesCombined,
    description: "Monthly product-group sales shares from Merlin invoices.",
    defaultHref: "/profit-loss",
    items: [{ href: "/profit-loss", label: "P&L's", icon: ChartNoAxesCombined }],
  },
  {
    key: "expenses",
    label: "Expenses",
    icon: Wallet,
    description: "Credit-card statements, receipts, matching, nominal codes.",
    defaultHref: "/expenses",
    items: [
      { href: "/expenses", label: "Dashboard", icon: LayoutDashboard, adminOnly: true },
      { href: "/statements", label: "Statements", icon: FileText, adminOnly: true },
      // Receipts is the only expenses page non-admin users can access.
      // They can upload receipts there; everything else is admin-only.
      { href: "/receipts", label: "Receipts", icon: Receipt },
      { href: "/match-failures", label: "Match Failures", icon: AlertTriangle, adminOnly: true },
      { href: "/supplier-templates", label: "Supplier Templates", icon: BookOpen, adminOnly: true },
      { href: "/nominal-templates", label: "Nominal Codes", icon: Hash, adminOnly: true },
      { href: "/cost-price-checks", label: "Cost Price Checks", icon: FileSearch, adminOnly: true },
    ],
  },
  {
    key: "upcoming_payments",
    label: "Upcoming Payments",
    icon: Calendar,
    description: "Review future overseas supplier payments.",
    defaultHref: "/upcoming-payments",
    accessGroupKey: "expenses",
    items: [{ href: "/upcoming-payments", label: "Upcoming Payments", icon: Calendar }],
  },
  {
    key: "contracts",
    label: "Contracts",
    icon: FileSignature,
    description: "Customer contract documents and renewals.",
    defaultHref: "/contracts",
    items: [
      { href: "/contracts", label: "Contracts", icon: FileSignature },
      { href: "/prepayments", label: "Prepayments", icon: Coins },
    ],
    extraRoutePrefixes: ["/prepayments"],
  },
  {
    key: "cash_receipts",
    label: "Cash Receipts",
    icon: Banknote,
    description: "BACS reconciliation, remittances, ID rules.",
    defaultHref: "/cash-receipts",
    items: [
      { href: "/cash-receipts", label: "Cash Receipts", icon: Banknote },
      { href: "/cash-receipts/proforma-payments", label: "Proforma Payments", icon: FileCheck },
      { href: "/cash-receipts/daily-batch", label: "Daily Batch Reconciliation", icon: ListChecks },
      { href: "/cash-receipts/rules", label: "ID Rules", icon: Settings2 },
      { href: "/cash-receipts/adjustments", label: "Manual Adjustments", icon: Undo2, adminOnly: true },
      { href: "/stripe-refunds", label: "Stripe Refunds", icon: RotateCcw, adminOnly: true },
      { href: "/credit-checks", label: "Credit Checks", icon: ShieldCheck },
      { href: "/cash-receipts/guide", label: "BACS Guide", icon: HelpCircle },
    ],
  },
  {
    key: "courier_invoices",
    label: "Courier Invoices",
    icon: Truck,
    description: "Audit courier invoices against rate cards and POD reports.",
    defaultHref: "/courier-invoices",
    items: [{ href: "/courier-invoices", label: "Courier Invoices", icon: Truck }],
  },
  {
    key: "collection_pods",
    label: "Collection PODs",
    icon: PackageSearch,
    description: "Search warehouse proof-of-delivery photos by order or date.",
    defaultHref: "/collection-pods",
    ungated: true,
    items: [{ href: "/collection-pods", label: "Collection PODs", icon: PackageSearch }],
  },
  {
    key: "email_log",
    label: "Email Log",
    icon: Mail,
    description: "View sent emails, delivery attempts, and message previews.",
    defaultHref: "/email-log",
    ungated: true,
    items: [{ href: "/email-log", label: "Email Log", icon: Mail }],
  },
  {
    key: "bank_reconciliation",
    label: "Bank Reconciliation",
    icon: Landmark,
    description: "Account 73662063 / nominal 17012 reconciliation (read).",
    defaultHref: "/bank-reconciliation",
    items: [{ href: "/bank-reconciliation", label: "Bank Reconciliation", icon: Landmark }],
  },
  {
    key: "bank_reconciliation_manage",
    label: "Bank Reconciliation: operate",
    icon: Landmark,
    description: "Upload CSVs, create months, match, note and confirm.",
    defaultHref: "/bank-reconciliation",
    navHidden: true,
    items: [],
  },
  {
    key: "bank_reconciliation_signoff",
    label: "Bank Reconciliation: sign-off",
    icon: Landmark,
    description: "Independently sign off and reopen months.",
    defaultHref: "/bank-reconciliation",
    navHidden: true,
    items: [],
  },
  {
    key: "users_settings",
    label: "Settings",
    icon: Settings,
    description: "Manage user accounts, permissions, and app settings.",
    defaultHref: "/users",
    items: [
      { href: "/users", label: "Users", icon: UsersIcon },
      { href: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

export const PAGE_GROUP_KEYS = PAGE_GROUPS.map((g) => g.key);

export function findGroupForPath(pathname: string): PageGroup | undefined {
  // Longest matching defaultHref / item href wins (so /cash-receipts/rules
  // beats /cash-receipts).
  let best: { group: PageGroup; len: number } | undefined;
  for (const group of PAGE_GROUPS) {
    for (const item of group.items) {
      if (pathname === item.href || pathname.startsWith(item.href + "/")) {
        if (!best || item.href.length > best.len) best = { group, len: item.href.length };
      }
    }
    for (const prefix of group.extraRoutePrefixes ?? []) {
      if (pathname === prefix || pathname.startsWith(prefix + "/")) {
        if (!best || prefix.length > best.len) best = { group, len: prefix.length };
      }
    }
  }
  return best?.group;
}

// Groups that support per-group admin (must mirror PAGE_ADMIN_GROUP_KEYS
// in artifacts/api-server/src/middlewares/auth.ts). A user whose
// `pageAdminPermissions` array contains one of these gets admin powers
// inside that group only — they see and use every admin page inside the
// group, but get no privileges in any other group and are not a global
// admin. Only super-admin can set this list.
export const PAGE_ADMIN_GROUP_KEYS = ["expenses"] as const;
export type PageAdminGroupKey = (typeof PAGE_ADMIN_GROUP_KEYS)[number];

export interface AccessUser {
  role?: string;
  realRole?: string;
  pagePermissions?: string[] | null;
  pageAdminPermissions?: string[] | null;
}

/**
 * Returns true if the (effective, post-"view as user") user can see a page-group.
 *
 * - admin / super_admin → every group (full bypass; group permissions are
 *   admin-driven, so being admin implies access).
 * - Anyone else → only groups whose key is in their pagePermissions array.
 *
 * In "view as user" mode the effective role is "user", so the admin sees the
 * preview restricted to their pagePermissions list — matching what the real
 * user would see.
 */
export function isAdminRole(role: string | undefined): boolean {
  return role === "admin" || role === "super_admin";
}

/**
 * True if the user has admin powers inside this specific page group —
 * either because they're a global admin/super-admin (covers every
 * group), or because the group's key appears in their
 * `pageAdminPermissions` list (per-group admin). Use this anywhere you
 * would otherwise gate on global admin within a group's pages.
 */
export function hasGroupAdmin(
  user: AccessUser | null | undefined,
  groupKey: PageGroupKey,
): boolean {
  if (!user) return false;
  if (isAdminRole(user.role)) return true;
  const perms = Array.isArray(user.pageAdminPermissions)
    ? user.pageAdminPermissions
    : [];
  return perms.includes(groupKey);
}

export function hasGroupAccess(user: AccessUser | null | undefined, group: PageGroup): boolean {
  if (!user) return false;
  // Ungated groups are open to every authenticated user.
  if (group.ungated) return true;
  if (isAdminRole(user.role)) return true;
  const perms = Array.isArray(user.pagePermissions) ? user.pagePermissions : [];
  const adminPerms = Array.isArray(user.pageAdminPermissions)
    ? user.pageAdminPermissions
    : [];
  // A per-group admin implicitly has access to the group, even if their
  // pagePermissions list doesn't include it.
  const accessKey = group.accessGroupKey ?? group.key;
  return perms.includes(accessKey) || adminPerms.includes(accessKey);
}

export function accessibleGroups(user: AccessUser | null | undefined): PageGroup[] {
  return PAGE_GROUPS.filter((g) => !g.navHidden && hasGroupAccess(user, g));
}

/**
 * Items inside a group the user can actually see — strips adminOnly items
 * unless the user has admin powers in this group (global admin OR
 * per-group admin via pageAdminPermissions). Currently only the Expenses
 * group uses adminOnly.
 */
export function accessibleItems(user: AccessUser | null | undefined, group: PageGroup): PageItem[] {
  const admin = hasGroupAdmin(user, group.key);
  return group.items.filter((it) => admin || !it.adminOnly);
}

/**
 * Where to land when the user clicks a group's tile / sidebar header. For
 * admins (global or per-group) this is the group's defaultHref; for
 * everyone else it's the first item they're actually allowed to see
 * (e.g. /receipts for an upload-only Expenses user).
 */
export function groupLandingHref(user: AccessUser | null | undefined, group: PageGroup): string {
  if (hasGroupAdmin(user, group.key)) return group.defaultHref;
  const items = accessibleItems(user, group);
  return items[0]?.href ?? group.defaultHref;
}
